import { MagnifyingGlassIcon } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import { useGetAnnouncementRecipients } from '@/api/communication/get-announcement-recipients'
import { ANNOUNCEMENT_RECIPIENTS_BASE_QUERY_KEY } from '@/api/communication/get-announcement-recipients/get-announcement-recipients'
import { usePersonColumnSettings } from '@/components/business/person-column'
import { TableGroup } from '@/components/layout/table-group'
import { StatusTabs } from '@/components/ui/status-tabs'
import { TextInput } from '@/components/ui/text-input'
import { DEFAULT_TZ } from '@/constants'
import { useFilters } from '@/hooks/use-filters'
import { useSearchParams } from '@/hooks/use-search-params'
import {
  getAnnouncementRecipientCounts,
  getAnnouncementRecipientsTotal,
} from '@/pages/communication/announcement-details/announcement-details.counts'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'

import {
  getSmsStatusOptions,
  getSmsTableColumns,
  SmsStatusFilter,
  smsRecipientsTableFiltersSchema,
  smsRecipientsTableInitialFilters,
} from './sms-recipients-table.constants'

export const SmsRecipientsTable = () => {
  const { t } = useTranslation(['communication'])
  const params = useParams()
  const activeBuilding = useSelector(selectActiveBuilding)

  const announcementId = Number(params.id)
  const buildingId = activeBuilding?.id
  const timezone = activeBuilding?.timezone || DEFAULT_TZ
  const searchParams = useSearchParams({
    initialState: smsRecipientsTableInitialFilters,
    schema: smsRecipientsTableFiltersSchema,
  })

  const tableFilters = useFilters({
    controlledState: searchParams.parsedQuery,
    initialState: smsRecipientsTableInitialFilters,
    onFiltersChange: searchParams.writeParams,
  })

  const { filters } = tableFilters
  const search = filters.search?.trim()
  const { data, isFetching, error } = useGetAnnouncementRecipients({
    buildingId,
    id: announcementId,
    limit: filters.limit,
    query: search || undefined,
    skip: filters.skip,
    sortBy: filters.sortBy,
    sortOrder: filters.sortOrder,
    status: filters.status === 'All' ? undefined : filters.status,
  })

  const countMap = getAnnouncementRecipientCounts(data)

  const smsStatusOptions = getSmsStatusOptions(t).map((option) => ({
    ...option,
    count: countMap[option.value],
  }))

  const residentColumnSettings = usePersonColumnSettings({
    column: 'resident',
    dashboard: 'Announcements',
    fieldType: 'resident',
    rowsQueryKey: [ANNOUNCEMENT_RECIPIENTS_BASE_QUERY_KEY],
  })
  const columns = getSmsTableColumns(t, timezone, residentColumnSettings)

  return (
    <TableGroup
      id="announcement-recipients-sms"
      columns={columns}
      data={data?.recipients || []}
      emptyNoFilters={{
        description: t(
          'communication:announcementDetails.recipientsTable.sms.empty.noFilters.description',
        ),
        title: t('communication:announcementDetails.recipientsTable.sms.empty.noFilters.title'),
      }}
      emptyWithFilters={{
        description: t(
          'communication:announcementDetails.recipientsTable.empty.withFilters.description',
        ),
        title: t('communication:announcementDetails.recipientsTable.empty.withFilters.title'),
      }}
      error={error}
      enableColumnVisibility={false}
      getRowKey={(recipient) => recipient.id}
      loading={isFetching}
      navigation={{
        search: (
          <TextInput
            clearable
            icon={MagnifyingGlassIcon}
            name="search"
            placeholder={t('communication:announcementDetails.recipientsTable.search.placeholder')}
            size="small"
            value={tableFilters.filters.search || ''}
            onChange={(e) => tableFilters.setFilter('search', e.target.value)}
          />
        ),
        statusTabs: (
          <StatusTabs<SmsStatusFilter>
            options={smsStatusOptions}
            value={filters.status || smsRecipientsTableInitialFilters.status || null}
            onChange={(value) => tableFilters.setFilter('status', value)}
          />
        ),
      }}
      pagination={{
        paginationFilters: tableFilters,
        total: getAnnouncementRecipientsTotal(countMap, filters.status),
      }}
      sortBy={tableFilters.getFilter('sortBy')}
      sortOrder={tableFilters.getFilter('sortOrder')}
      tableFilters={tableFilters}
    />
  )
}
